import axios from 'axios';
import { OPENAI_API_KEY } from '../config/constants';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const analyzeEssay = async (essay, prompt) => {
  if (!OPENAI_API_KEY) {
    console.error('OpenAI API key is missing');
    throw new Error('API key not configured');
  }

  let attempts = 0;
  while (attempts < MAX_RETRIES) {
    try {
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your connection and try again.');
      }

      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: "gpt-4",
          messages: [
            {
              role: "system",
              content: `You are an expert TOEFL writing examiner with years of experience. 
              Analyze the following essay based on official TOEFL criteria and provide a detailed evaluation in JSON format:
              {
                "overall": number (0-30),
                "categories": {
                  "taskAchievement": number (0-5),
                  "coherence": number (0-5),
                  "vocabulary": number (0-5),
                  "grammar": number (0-5)
                },
                "strengths": [
                  "detailed strength point 1",
                  "detailed strength point 2",
                  ...
                ],
                "improvements": [
                  "specific improvement suggestion 1",
                  "specific improvement suggestion 2",
                  ...
                ],
                "feedback": "detailed paragraph explaining the overall assessment",
                "vocabularyHighlights": [
                  "sophisticated word/phrase used",
                  ...
                ],
                "grammarIssues": [
                  "specific grammar error with correction",
                  ...
                ]
              }
              
              Provide specific, actionable feedback that will help the student improve their writing.`
            },
            {
              role: "user",
              content: `Prompt: ${prompt}\n\nEssay: ${essay}`
            }
          ],
          temperature: 0.7,
          max_tokens: 2000,
          presence_penalty: 0.3,
          frequency_penalty: 0.3
        },
        {
          headers: {
            'Authorization': `Bearer ${OPENAI_API_KEY}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );

      const feedbackContent = response.data.choices[0].message.content;
      try {
        const parsedFeedback = JSON.parse(feedbackContent);
        
        if (!parsedFeedback.overall || !parsedFeedback.categories) {
          throw new Error('Invalid response structure');
        }

        return parsedFeedback;
      } catch (parseError) {
        console.error('Error parsing feedback:', feedbackContent);
        throw new Error('Invalid response format from GPT-4');
      }

    } catch (error) {
      attempts++;
      console.error(`Attempt ${attempts} failed:`, error);

      if (error.response?.status === 401) {
        throw new Error('Invalid API key or insufficient permissions for GPT-4 access.');
      }

      if (error.response?.status === 429) {
        throw new Error('Rate limit exceeded. Please try again in a few moments.');
      }

      if (error.response?.status === 404) {
        throw new Error('GPT-4 access not available. Please check your API subscription.');
      }

      if (attempts < MAX_RETRIES) {
        await sleep(RETRY_DELAY * attempts);
        continue;
      }

      throw new Error(
        'Unable to analyze essay. ' +
        'Please ensure you have GPT-4 access and try again. ' +
        `(${error.message})`
      );
    }
  }
}; 